import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import { PageHeader, Panel, selectClass } from '../components/OpsPageKit';

// Job Imports (29/10/2026, on trial): pull the jobs from ODIN so the
// coordinators don't have to. Log in to ODIN once (the password goes straight
// through to ODIN and isn't kept; the server keeps ODIN's session for up to ~7
// days), pick the dates, and the page reads ODIN's work-order list in the
// background (left) next to the same dates from Netomnia's API (right).
const STAMP = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const uk = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const dayFrom = (n) => new Date(Date.now() + n * 864e5).toLocaleDateString('en-CA');
const inputClass = 'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#711C91] focus:outline-none focus:ring-1 focus:ring-[#711C91]';
const btn = 'rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-50';
const primary = `${btn} bg-[#711C91] text-white hover:bg-[#5c1676]`;
const secondary = `${btn} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
const errorOf = (err) => err.response?.data?.error || err.message;
const keyOf = (j) => `${j.date}|${j.wo}`;
const daysIn = (from, to) => {
  const out = [];
  for (let d = new Date(`${from}T12:00:00Z`); d.toISOString().slice(0, 10) <= to; d.setUTCDate(d.getUTCDate() + 1)) out.push(d.toISOString().slice(0, 10));
  return out;
};

const PRESETS = () => [
  { id: 'tomorrow', label: `Tomorrow (${uk(dayFrom(1))})`, from: dayFrom(1), to: dayFrom(1) },
  { id: 'today', label: `Today (${uk(dayFrom(0))})`, from: dayFrom(0), to: dayFrom(0) },
  { id: 'week', label: `Next 7 days (${uk(dayFrom(1))} to ${uk(dayFrom(7))})`, from: dayFrom(1), to: dayFrom(7) },
  { id: 'custom', label: 'Choose dates…' },
];

function LogIn({ onClose, onDone }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfa, setMfa] = useState(null); // { method, userId }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setNote('');
    try {
      const { data } = await api.post('/api/job-imports/odin/log-in', { email, password, mfaCode: mfa ? code : undefined });
      if (data.mfa) { setMfa({ method: data.method, userId: data.userId }); setCode(''); } else onDone(data);
    } catch (err) { setError(errorOf(err)); } finally { setBusy(false); }
  };
  const resend = async () => {
    setError(''); setNote('');
    try { await api.post('/api/job-imports/odin/resend', { userId: mfa.userId }); setNote('A new code is on its way.'); } catch (err) { setError(errorOf(err)); }
  };

  return (
    <Modal title="Log in to ODIN" subtitle="The password goes straight to ODIN and isn’t kept here." onClose={onClose} width="max-w-md">
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">ODIN email
          <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!mfa} className={inputClass} autoFocus required />
        </label>
        <label className="block text-sm font-medium text-gray-700">Password
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={!!mfa} className={inputClass} required />
        </label>
        {mfa ? (
          <label className="block text-sm font-medium text-gray-700">
            {mfa.method === 'totp' ? 'Code from the authenticator app' : 'Code ODIN emailed'}
            <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className={inputClass} autoFocus required />
            {mfa.method !== 'totp' ? <button type="button" onClick={resend} className="mt-1 text-xs font-semibold text-[#711C91] hover:underline">Send a new code</button> : null}
          </label>
        ) : null}
        {error ? <p className="text-sm text-rose-700">{error}</p> : null}
        {note ? <p className="text-sm text-emerald-700">{note}</p> : null}
        <p className="text-xs text-gray-500">ODIN records every job read under this login.</p>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={mfa ? () => { setMfa(null); setCode(''); } : onClose} className={secondary}>{mfa ? 'Back' : 'Cancel'}</button>
          <button type="submit" disabled={busy} className={primary}>{busy ? 'Logging in…' : 'Log in'}</button>
        </div>
      </form>
    </Modal>
  );
}

function Connection({ state, onLogIn, onLogOut, busy }) {
  if (!state) return <p className="px-4 py-3 text-sm text-gray-500">Loading…</p>;
  if (!state.signedIn) {
    return (
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold text-gray-900">Not logged in to ODIN</p>
          <p className="text-gray-500">{state.lastError || 'Log in with an ODIN account to pull its jobs.'}</p>
        </div>
        <button type="button" onClick={onLogIn} className={primary}>Log in</button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-semibold text-emerald-800">Logged in to ODIN</p>
        <p className="text-gray-500">
          By {state.signedInBy || 'someone'} on {STAMP(state.signedInAt)}.
          {state.refreshExpiresAt ? <> Stays logged in until <span className="font-medium text-gray-700">{STAMP(state.refreshExpiresAt)}</span>.</> : null}
        </p>
        {state.lastError ? <p className="text-rose-700">{state.lastError}</p> : null}
      </div>
      <button type="button" onClick={onLogOut} disabled={busy} className={secondary}>{busy ? 'Logging out…' : 'Log out'}</button>
    </div>
  );
}

function DatePicker({ range, onRange }) {
  const presets = useMemo(PRESETS, []);
  const [preset, setPreset] = useState('tomorrow');
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const pick = (id) => {
    setPreset(id);
    const p = presets.find((x) => x.id === id);
    if (p.from) onRange({ from: p.from, to: p.to });
  };
  const apply = () => { if (from && to && to >= from) onRange({ from, to }); };
  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="text-xs font-medium text-gray-500">Jobs for
        <select value={preset} onChange={(e) => pick(e.target.value)} className={`${selectClass} mt-1 block`}>
          {presets.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </label>
      {preset === 'custom' ? (
        <>
          <label className="text-xs font-medium text-gray-500">From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${selectClass} mt-1 block`} />
          </label>
          <label className="text-xs font-medium text-gray-500">To
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className={`${selectClass} mt-1 block`} />
          </label>
          <button type="button" onClick={apply} disabled={!from || !to || to < from} className={`${primary} py-1.5`}>Show</button>
        </>
      ) : null}
    </div>
  );
}

// Who the job is for, from ODIN's Provider (29/10: BRSK, NETOMNIA,
// NETOMNIA_LEGACY, PYOMNI). The API side has no provider: it borrows ODIN's.
function ProviderMark({ provider }) {
  const p = String(provider || '').toUpperCase();
  if (p.startsWith('NETOMNIA')) {
    return (
      <span className="inline-flex items-center gap-1" title={p === 'NETOMNIA' ? 'Netomnia' : 'Netomnia (legacy)'}>
        <img src="/providers/netomnia.png" alt="Netomnia" className="h-3" />
        {p !== 'NETOMNIA' ? <span className="text-[10px] text-gray-500">legacy</span> : null}
      </span>
    );
  }
  if (p === 'BRSK') return <img src="/providers/brsk.png" alt="BRSK" title="BRSK" className="h-4 w-4" />;
  if (p) return <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">{provider}</span>;
  return null;
}

// The expanded job: every field, ODIN and the API next to each other. A field
// both sides have but disagree on is flagged; one only ODIN has (schedule,
// provider, team, position) just shows ODIN's.
const FIELDS = [
  ['Date', (j) => uk(j.date)],
  ['Type', (j) => j.type],
  ['Stage', (j) => j.stage],
  ['Connection', (j) => j.connection],
  ['Slot', (j) => j.slot],
  ['Address', (j) => j.address],
  ['Postcode', (j) => j.postcode],
  ['CBT (L4)', (j) => j.l4],
  ['Engineer', (j) => j.engineer],
  ['Schedule', (j) => j.schedule, true],
  ['Provider', (j) => j.provider, true],
  ['Team', (j) => j.team, true],
];
const same = (a, b) => String(a ?? '').trim().toUpperCase() === String(b ?? '').trim().toUpperCase();

function JobDetail({ odinJob, apiJob }) {
  const diffs = odinJob && apiJob ? FIELDS.filter(([, get, odinOnly]) => !odinOnly && !same(get(odinJob), get(apiJob))).length : 0;
  const pos = odinJob?.latitude && odinJob?.longitude ? `${Number(odinJob.latitude).toFixed(5)}, ${Number(odinJob.longitude).toFixed(5)}` : null;
  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-white" onClick={(e) => e.stopPropagation()}>
      <table className="w-full text-xs">
        <thead className="bg-gray-50 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          <tr><th className="px-2 py-1.5" /><th className="px-2 py-1.5">ODIN</th><th className="px-2 py-1.5">Netomnia API</th></tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {FIELDS.map(([label, get, odinOnly]) => {
            const o = odinJob ? get(odinJob) : null;
            const a = apiJob ? get(apiJob) : null;
            const differs = odinJob && apiJob && !odinOnly && !same(o, a);
            return (
              <tr key={label} className={differs ? 'bg-rose-50' : ''}>
                <th className="w-24 whitespace-nowrap px-2 py-1 text-left font-medium text-gray-500">{label}</th>
                <td className={`px-2 py-1 ${differs ? 'font-semibold text-rose-800' : 'text-gray-800'}`}>{odinJob ? (o || '—') : <span className="text-gray-400">not in ODIN</span>}</td>
                <td className={`px-2 py-1 ${differs ? 'font-semibold text-rose-800' : 'text-gray-800'}`}>
                  {apiJob ? (odinOnly ? <span className="text-gray-400">not sent</span> : (a || '—')) : <span className="text-gray-400">not in the API</span>}
                </td>
              </tr>
            );
          })}
          <tr>
            <th className="px-2 py-1 text-left font-medium text-gray-500">Position</th>
            <td className="px-2 py-1 text-gray-800" colSpan={2}>
              {pos ? <a href={`https://www.google.com/maps?q=${odinJob.latitude},${odinJob.longitude}`} target="_blank" rel="noreferrer" className="font-medium text-[#711C91] hover:underline">{pos}</a> : '—'}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 px-2 py-1.5 text-xs">
        {odinJob && apiJob ? <span className={diffs ? 'font-semibold text-rose-700' : 'text-emerald-700'}>{diffs ? `${diffs} field${diffs === 1 ? '' : 's'} differ` : 'ODIN and the API agree'}</span> : null}
        {odinJob?.id ? (
          <a href={`https://netomnia.app.odin.substantial.group/FieldServiceModule/WorkOrder/${odinJob.id}`} target="_blank" rel="noreferrer" className="ml-auto font-semibold text-[#711C91] hover:underline">Open in ODIN</a>
        ) : null}
      </div>
    </div>
  );
}

function JobRow({ j, many, missing, odin, provider, match, open, onToggle }) {
  return (
    <li className={`cursor-pointer px-3 py-2 text-xs hover:bg-purple-50/60 ${missing ? 'bg-amber-50' : ''} ${open ? 'bg-purple-50/60' : ''}`} onClick={onToggle}>
      <div className="flex flex-wrap items-center gap-x-2">
        <ProviderMark provider={provider} />
        <span className="font-semibold text-gray-900">{j.wo}</span>
        <span className="text-gray-500">{[many ? uk(j.date) : null, j.type, j.connection, j.slot].filter(Boolean).join(' · ')}</span>
        {missing ? <span className="ml-auto rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">{missing}</span> : null}
        <span className={`${missing ? '' : 'ml-auto'} text-gray-400`} aria-hidden="true">{open ? '▴' : '▾'}</span>
      </div>
      <p className="text-gray-700">{j.address || '(no address)'}</p>
      <p className="text-gray-500">
        {odin
          ? [j.schedule, j.engineer, j.l4 ? `CBT ${j.l4}` : null].filter(Boolean).join(' · ') || '—'
          : [j.stage, j.engineer, j.l4 ? `CBT ${j.l4}` : null].filter(Boolean).join(' · ') || '—'}
      </p>
      {open ? <JobDetail odinJob={odin ? j : match} apiJob={odin ? match : j} /> : null}
    </li>
  );
}

// Days done out of days asked for; inside a day ODIN gives no progress, so the
// bar's current slice pulses until that day comes back.
function Progress({ done, total, day, jobs }) {
  const pct = Math.round((done / total) * 100);
  return (
    <div className="px-4 py-3">
      <div className="flex justify-between text-xs text-gray-600">
        <span>Reading ODIN in the background: {total > 1 ? `day ${done + 1} of ${total}, ` : ''}{uk(day)}…</span>
        <span className="tabular-nums">{jobs ? `${jobs} jobs so far · ` : ''}{pct}%</span>
      </div>
      <div className="relative mt-1.5 h-2.5 overflow-hidden rounded-full bg-gray-100">
        <div className="absolute inset-y-0 left-0 rounded-full bg-[#711C91] transition-all duration-500" style={{ width: `${pct}%` }} />
        <div className="absolute inset-y-0 animate-pulse rounded-full bg-[#711C91]/40" style={{ left: `${pct}%`, width: `${100 / total}%` }} />
      </div>
    </div>
  );
}

function Side({ title, note, loading, error, jobs, other, many, odin, onlyDiff, type, progress }) {
  const otherByKey = useMemo(() => (other ? new Map(other.map((j) => [keyOf(j), j])) : null), [other]);
  const [open, setOpen] = useState(() => new Set());
  const toggle = (k) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const label = odin ? 'Not in the API' : 'Not in ODIN';
  const rows = (jobs || []).filter((j) => (!type || j.type === type) && (!onlyDiff || (otherByKey && !otherByKey.has(keyOf(j)))));
  return (
    <Panel title={jobs ? `${title} · ${jobs.length} jobs` : title} right={note ? <span className="text-xs text-gray-500">{note}</span> : null}>
      {error ? <p className="px-4 py-3 text-sm text-rose-700">{error}</p> : null}
      {loading && !error && progress ? <Progress {...progress} /> : null}
      {loading && !error && !progress ? <p className="px-4 py-3 text-sm text-gray-500">Loading…</p> : null}
      {!loading && !error && jobs && !rows.length ? <p className="px-4 py-3 text-sm text-gray-500">{onlyDiff ? 'Nothing missing.' : 'No jobs.'}</p> : null}
      {!loading && rows.length ? (
        <ul className="max-h-[65vh] divide-y divide-gray-100 overflow-auto">
          {rows.map((j) => (
            <JobRow key={keyOf(j)} j={j} many={many} odin={odin} missing={otherByKey && !otherByKey.has(keyOf(j)) ? label : null}
              provider={odin ? j.provider : otherByKey?.get(keyOf(j))?.provider}
              match={otherByKey?.get(keyOf(j))} open={open.has(keyOf(j))} onToggle={() => toggle(keyOf(j))} />
          ))}
        </ul>
      ) : null}
    </Panel>
  );
}

function Jobs({ range }) {
  const [odin, setOdin] = useState({ loading: true });
  const [apiSide, setApiSide] = useState({ loading: true });
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [type, setType] = useState('');

  // ODIN is read a day per request, so the progress bar can count the days.
  // A newer read (other dates, Read again) makes the older one stop.
  // Other contractors' jobs JP's login sees: left out, said how many.
  const otherNote = (others) => {
    const list = Object.entries(others || {}).sort((a, b) => b[1] - a[1]);
    if (!list.length) return '';
    const label = (k) => ({ VEA_TELECOMMS: 'VEA Telecoms', NETOMNIA: 'Netomnia', S_M: 'S&M', GNS_INSTALLS: 'GNS' }[k] || k);
    return ` · Nano Fibre only; left out ${list.map(([k, n]) => `${n} ${label(k)}`).join(', ')}`;
  };
  const runRef = useRef(0);
  const load = useCallback(async () => {
    const params = { from: range.from, to: range.to };
    const run = runRef.current + 1;
    runRef.current = run;
    const days = daysIn(range.from, range.to);
    setApiSide({ loading: true });
    setOdin({ loading: true, progress: { done: 0, total: days.length, day: days[0] } });
    api.get('/api/job-imports/api-jobs', { params, timeout: 120000 }).then(
      ({ data }) => { if (runRef.current === run) setApiSide({ data }); },
      (err) => { if (runRef.current === run) setApiSide({ error: errorOf(err) }); });
    const jobs = [];
    const others = {};
    try {
      for (let i = 0; i < days.length; i += 1) {
        if (runRef.current !== run) return;
        setOdin({ loading: true, progress: { done: i, total: days.length, day: days[i], jobs: jobs.length } });
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get('/api/job-imports/odin/jobs', { params: { from: days[i], to: days[i] }, timeout: 120000 });
        jobs.push(...data.jobs);
        Object.entries(data.others || {}).forEach(([k, n]) => { others[k] = (others[k] || 0) + n; });
      }
      if (runRef.current === run) setOdin({ data: { jobs, others, readAt: new Date() } });
    } catch (err) {
      if (runRef.current === run) setOdin({ error: errorOf(err) });
    }
  }, [range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  const left = odin.data?.jobs;
  const right = apiSide.data?.jobs;
  const many = range.from !== range.to;
  const types = useMemo(() => [...new Set([...(left || []), ...(right || [])].map((j) => j.type).filter(Boolean))].sort(), [left, right]);
  const counts = useMemo(() => {
    if (!left || !right) return null;
    const r = new Set(right.map(keyOf));
    const l = new Set(left.map(keyOf));
    return { both: left.filter((j) => r.has(keyOf(j))).length, onlyOdin: left.filter((j) => !r.has(keyOf(j))).length, onlyApi: right.filter((j) => !l.has(keyOf(j))).length };
  }, [left, right]);

  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700">
        {counts
          ? <span><b>{counts.both}</b> in both · <b className="text-amber-700">{counts.onlyOdin}</b> only in ODIN · <b className="text-amber-700">{counts.onlyApi}</b> only in the API</span>
          : <span className="text-gray-500">Comparing once both sides have loaded…</span>}
        <label className="ml-auto flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} /> Only the differences
        </label>
        {types.length > 1 ? (
          <select value={type} onChange={(e) => setType(e.target.value)} className={selectClass} aria-label="Job type">
            <option value="">All types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        ) : null}
        <button type="button" onClick={load} className={`${secondary} py-1.5`}>Read again</button>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Side title="From ODIN" note={odin.data ? `Read ${STAMP(odin.data.readAt)}${otherNote(odin.data.others)}` : null} loading={odin.loading} error={odin.error} progress={odin.progress}
          jobs={left} other={right} many={many} odin onlyDiff={onlyDiff} type={type} />
        <Side title="From the Netomnia API" note={apiSide.data?.readAt ? `Read ${STAMP(apiSide.data.readAt)}` : null} loading={apiSide.loading} error={apiSide.error}
          jobs={right} other={left} many={many} odin={false} onlyDiff={onlyDiff} type={type} />
      </div>
    </>
  );
}

export default function JobImports() {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [logIn, setLogIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState({ from: dayFrom(1), to: dayFrom(1) });

  const load = useCallback(() => {
    setError('');
    api.get('/api/job-imports/odin').then((r) => setState(r.data), (err) => setError(errorOf(err)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const logOut = async () => {
    setBusy(true);
    try { setState((await api.post('/api/job-imports/odin/log-out')).data); } catch (err) { setError(errorOf(err)); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Job Imports" subtitle="Pull the jobs from ODIN, next to the same dates from the Netomnia API." onRefresh={load} />
      {error ? <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</p> : null}
      <div className="mt-4">
        <Panel title="ODIN">
          <Connection state={state} onLogIn={() => setLogIn(true)} onLogOut={logOut} busy={busy} />
        </Panel>
      </div>
      {state?.signedIn ? (
        <div className="mt-4">
          <DatePicker range={range} onRange={setRange} />
          <Jobs range={range} />
        </div>
      ) : null}
      {logIn ? <LogIn onClose={() => setLogIn(false)} onDone={(s) => { setLogIn(false); setState(s); }} /> : null}
    </div>
  );
}
